import * as React from 'react';
import { st, classes } from './PaginationCore.st.css';
import { DataHooks, PaginationCoreMode } from './constants';
import { calculateWidth, getId, measureAndSetRootMinWidth } from './utils';
import { PageStrip } from './PageStrip';
import { filterDataProps } from '../../utils/propsUtils';

const upperCaseFirst = (str: string): string =>
  str[0].toUpperCase() + str.slice(1);

const enum ButtonType {
  Prev = 'previous',
  Next = 'next',
  First = 'first',
  Last = 'last',
}

export interface PaginationCoreProps {
  /** hook for testing purposes */
  'data-hook'?: string;
  // data
  totalPages: number;
  currentPage?: number;
  // props
  pageUrl?(pageNumber: number): string;
  onChange?(event: { event: React.SyntheticEvent; page: number }): void;
  onClick?(event: React.SyntheticEvent): void;
  onDoubleClick?(event: React.SyntheticEvent): void;
  onMouseEnter?(event: React.SyntheticEvent): void;
  onMouseLeave?(event: React.SyntheticEvent): void;
  paginationMode?: 'pages' | 'input' | 'compact' | PaginationCoreMode;
  showFirstLastNavButtons?: boolean;
  firstLabel?: React.ReactNode;
  previousLabel?: React.ReactNode;
  nextLabel?: React.ReactNode;
  lastLabel?: React.ReactNode;
  gapLabel?: React.ReactNode;
  slashLabel?: React.ReactNode;
  rtl?: boolean;
  width?: number;
  showFirstPage?: boolean;
  showLastPage?: boolean;
  showNextLabel?: boolean;
  showPreviousLabel?: boolean;
  showInputModeTotalPages?: boolean;
  responsive?: boolean;
  maxPagesToShow?: number;
  className?: string;
  id?: string;
  updateResponsiveLayout?(callback: () => void): void;
  style?: React.CSSProperties;
  disabled?: boolean;
}

export interface PaginationCoreState {
  pageInputValue: string;
  pageInputHasError: boolean;
}

export default class PaginationCore extends React.Component<
  PaginationCoreProps,
  PaginationCoreState
> {
  static displayName = 'PaginationCore';
  public static defaultProps: Partial<PaginationCoreProps> = {
    currentPage: 1,
    showFirstLastNavButtons: false,
    showFirstPage: false,
    showLastPage: false,
    showNextLabel: true,
    showPreviousLabel: true,
    responsive: false,
    paginationMode: 'pages',
    showInputModeTotalPages: false,
    disabled: false,

    // dir="rtl" automatically flips the direction of less-than and more-than signs.
    // If we decide to use different labels we need to add conditional logic.
    firstLabel: '<<',
    lastLabel: '>>',
    previousLabel: '<',
    nextLabel: '>',
    gapLabel: '...',
    slashLabel: '\u00A0/\u00A0',
  };

  private rootNode: HTMLElement | null | undefined;

  private updateRootMinWidth() {
    this.rootNode &&
      this.props.paginationMode &&
      measureAndSetRootMinWidth(
        this.rootNode,
        this.props.paginationMode,
        this.props.id,
      );
  }

  public componentDidMount() {
    this.props.updateResponsiveLayout && this.updateRootMinWidth();
  }

  public componentDidUpdate() {
    this.props.updateResponsiveLayout && this.updateRootMinWidth();
  }

  private getId(elementName: string = ''): string | undefined {
    return getId(this.props.id, elementName);
  }

  private readonly getMaxPagesToShow = (): number => {
    if (this.props.maxPagesToShow) {
      return this.props.maxPagesToShow;
    }
    if (this.props.responsive) {
      return 20;
    }
    return 7;
  };

  public state = {
    pageInputValue: String(this.props.currentPage),
    pageInputHasError: false,
  };

  private renderPageStrip(): React.ReactElement {
    return (
      <PageStrip
        id={this.props.id}
        totalPages={this.props.totalPages || 0}
        currentPage={this.props.currentPage || 0}
        maxPagesToShow={this.getMaxPagesToShow() || 0}
        showFirstPage={!!this.props.showFirstPage}
        showLastPage={!!this.props.showLastPage}
        responsive={!!this.props.responsive}
        pageUrl={this.props.pageUrl}
        gapLabel={this.props.gapLabel || ''}
        onPageClick={this.handlePageClick}
        onPageKeyDown={this.handlePageKeyDown}
        updateResponsiveLayout={this.props.updateResponsiveLayout}
        disabled={!!this.props.disabled}
      />
    );
  }

  private readonly handlePageInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    this.setState({
      pageInputValue: e.target.value,
      pageInputHasError: false,
    });
  };

  private readonly handlePageInputKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    // Enter
    if (event.key === 'Enter') {
      const page = Number(this.state.pageInputValue);
      if (page !== this.props.currentPage) {
        if (1 <= page && page <= this.props.totalPages) {
          this.props.onChange && this.props.onChange({ event, page });
        } else {
          this.setState({ pageInputHasError: true });
        }
      }
    }
  };

  private readonly handlePageInputBlur = (): void => {
    this.setState({
      pageInputValue: String(this.props.currentPage),
      pageInputHasError: false,
    });
  };

  private readonly handlePageClick = (
    event: React.MouseEvent,
    page: number,
  ): void => {
    this.props.onChange && this.props.onChange({ event, page });
  };

  private readonly handlePageKeyDown = (
    event: React.KeyboardEvent,
    page: number,
  ): void => {
    // Enter or Space
    if (event.key === 'Enter' || event.key === 'Space') {
      this.props.onChange && this.props.onChange({ event, page });
    }
  };

  private renderPages(): React.ReactElement {
    switch (this.props.paginationMode) {
      case PaginationCoreMode.Input:
        return this.renderPageForm();
      case PaginationCoreMode.Compact:
        return this.renderPageCompact();
      case PaginationCoreMode.Pages:
      default:
        return this.renderPageStrip();
    }
  }

  private renderPageCompact(): React.ReactElement {
    return (
      <div
        data-hook={DataHooks.pageCompact}
        id={this.getId('pageCompact')}
        className={classes.compact}
      >
        <span id={this.getId('currentPage')} data-hook={DataHooks.currentPage}>
          {this.props.currentPage}
        </span>
        <span
          id={this.getId('slash')}
          className={classes.slash}
          data-hook={DataHooks.slashLabel}
        >
          {this.props.slashLabel}
        </span>
        <span id={this.getId('totalPages')} data-hook={DataHooks.totalPages}>
          {this.props.totalPages}
        </span>
      </div>
    );
  }

  private renderPageForm(): React.ReactElement {
    return (
      <div
        data-hook={DataHooks.pageForm}
        id={this.getId('pageForm')}
        className={classes.pageForm}
        dir="ltr"
      >
        <input
          id={this.getId('pageInput')}
          data-hook={DataHooks.pageInput}
          type="number"
          className={classes.pageInput}
          min={1}
          max={this.props.totalPages}
          value={this.state.pageInputValue}
          disabled={this.props.disabled}
          onChange={this.handlePageInputChange}
          onKeyDown={this.handlePageInputKeyDown}
          aria-label={
            'Page number, select a number between 1 and ' +
            this.props.totalPages
          }
          onBlur={this.handlePageInputBlur}
          style={{ width: calculateWidth(this.props.totalPages) }}
        />
        {this.props.showInputModeTotalPages && [
          <span key="slash" id={this.getId('slash')} className={classes.slash}>
            {this.props.slashLabel}
          </span>,
          <span
            key="total-pages"
            id={this.getId('totalPages')}
            data-hook={DataHooks.totalPages}
            className={classes.totalPages}
          >
            {this.props.totalPages}
          </span>,
        ]}
      </div>
    );
  }

  private renderEmptyButton(type: ButtonType): React.ReactElement {
    let btnClass = '';
    switch (type) {
      case ButtonType.Prev: {
        btnClass = classes.emptyButtonPrevious;
        break;
      }
      case ButtonType.Next: {
        btnClass = classes.emptyButtonNext;
        break;
      }
      default:
    }

    return (
      <div
        data-hook={DataHooks[type]}
        className={st(classes.emptyButton, btnClass)}
      />
    );
  }

  private renderNavButton(type: ButtonType): React.ReactElement {
    const { currentPage, totalPages, pageUrl } = this.props;

    const disabled =
      this.props.disabled ||
      ((type === ButtonType.First || type === ButtonType.Prev) &&
        (currentPage || 0) <= 1) ||
      ((type === ButtonType.Last || type === ButtonType.Next) &&
        (currentPage || 0) >= totalPages);

    const [btnClass, label, page] = {
      [ButtonType.Prev]: [
        classes.navButtonPrevious,
        this.props.previousLabel,
        (currentPage || 0) - 1,
      ],
      [ButtonType.Next]: [
        classes.navButtonNext,
        this.props.nextLabel,
        (currentPage || 0) + 1,
      ],
      [ButtonType.First]: [classes.navButtonFirst, this.props.firstLabel, 1],
      [ButtonType.Last]: [
        classes.navButtonLast,
        this.props.lastLabel,
        totalPages,
      ],
    }[type] as any;

    return (
      <a
        data-hook={DataHooks[type]}
        id={this.getId('navButton' + upperCaseFirst(type))}
        className={st(classes.navButton, { disabled }, btnClass)}
        aria-label={upperCaseFirst(type) + ' Page'}
        aria-disabled={disabled}
        tabIndex={disabled || pageUrl ? undefined : 0}
        onClick={
          disabled ? undefined : event => this.handlePageClick(event, page)
        }
        onKeyDown={
          disabled ? undefined : event => this.handlePageKeyDown(event, page)
        }
        href={!disabled && pageUrl ? pageUrl(page) : undefined}
      >
        {label}
      </a>
    );
  }

  public UNSAFE_componentWillReceiveProps(nextProps: PaginationCoreProps) {
    this.setState({
      pageInputValue: String(nextProps.currentPage),
      pageInputHasError: false,
    });
  }

  render() {
    const {
      disabled,
      showFirstLastNavButtons,
      showNextLabel,
      showPreviousLabel,
      width,
      style: inlineStyle,
    } = this.props;

    const styleStates = {
      disabled,
      error: this.state.pageInputHasError,
    };

    return (
      <nav
        ref={el => (this.rootNode = el)}
        id={this.getId('')}
        aria-label="PaginationCore Navigation"
        aria-disabled={disabled}
        dir={this.props.rtl ? 'rtl' : undefined}
        onClick={this.props.onClick}
        onDoubleClick={this.props.onDoubleClick}
        onMouseEnter={this.props.onMouseEnter}
        onMouseLeave={this.props.onMouseLeave}
        style={inlineStyle || { width }}
        className={st(classes.root, styleStates, this.props.className)}
        {...filterDataProps(this.props)}
      >
        {showFirstLastNavButtons && this.renderNavButton(ButtonType.First)}
        {showPreviousLabel
          ? this.renderNavButton(ButtonType.Prev)
          : this.renderEmptyButton(ButtonType.Prev)}
        {this.renderPages()}
        {showNextLabel
          ? this.renderNavButton(ButtonType.Next)
          : this.renderEmptyButton(ButtonType.Next)}
        {showFirstLastNavButtons && this.renderNavButton(ButtonType.Last)}
      </nav>
    );
  }
}
